import { useState, useEffect } from 'react';
import { getSession, onAuthStateChange } from '@/features/auth';
import type { Session, User } from '@supabase/supabase-js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseAuthReturn {
  session: Session | null;
  user: User | null;
  loading: boolean;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Tracks the current Supabase auth state.
 *
 * On mount the hook fetches the existing session via `getSession()` and then
 * subscribes to auth-state changes via `onAuthStateChange()`. The subscription
 * is cleaned up automatically when the consuming component unmounts.
 *
 * Do NOT call Supabase directly from here — all auth logic is delegated to
 * the service layer in `src/features/auth/index.ts`.
 */
export function useAuth(): UseAuthReturn {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // 1. Fetch the current session on mount
    async function fetchInitialSession() {
      const result = await getSession();

      if (cancelled) return;

      if (result.error) {
        // Log but don't crash — the user is simply unauthenticated.
        console.error('[useAuth] Failed to fetch initial session:', result.error.message);
        setSession(null);
        setUser(null);
      } else {
        setSession(result.data?.session ?? null);
        setUser(result.data?.user ?? null);
      }

      setLoading(false);
    }

    fetchInitialSession();

    // 2. Subscribe to auth state changes
    const unsubscribe = onAuthStateChange(({ session: newSession, user: newUser }) => {
      if (cancelled) return;
      setSession(newSession);
      setUser(newUser);
      setLoading(false);
    });

    // 3. Cleanup
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  return { session, user, loading };
}
